/**
 * AI Trial Room — Admin Products Manager
 * Add/delete apparel/jewelry/salon items with image URL + retailer tag
 */
import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Trash2, Plus, RefreshCw, Store, Users, DollarSign, Clock, TrendingUp, Image as ImageIcon, Zap, Save } from 'lucide-react';
import api from '../../utils/api';

const CATEGORIES = [
  { id: 'apparels', label: '👕 Apparels & Fashion', color: 'from-fuchsia-500 to-rose-500' },
  { id: 'jewelry',  label: '💎 Jewelry & Accessories', color: 'from-amber-500 to-yellow-500' },
  { id: 'salon',    label: '💇 Hair & Cosmetics', color: 'from-emerald-500 to-teal-500' },
];

const fmt = (n) => Number(n || 0).toLocaleString('en-IN');

export default function TrialRoomAdmin() {
  const [dash, setDash] = useState(null);
  const [subs, setSubs] = useState([]);
  const [products, setProducts] = useState([]);
  const [activeCat, setActiveCat] = useState('apparels');
  const [form, setForm] = useState({ category: 'apparels', subcategory: '', name: '', image_url: '', price: '', retailer: '', tags: '', sort_order: 100 });
  const [busy, setBusy] = useState(false);
  const [settings, setSettings] = useState({ ai_model: 'gpt-4o-mini', shop_name: 'Anant Trial Room', shop_logo_url: '', watermark_enabled: true, admin_whatsapp: '+919579369830' });
  const [availableModels, setAvailableModels] = useState({});
  const [usage, setUsage] = useState(null);
  const [wmForm, setWmForm] = useState({ shop_name: 'Anant Trial Room', shop_logo_url: '', watermark_enabled: true });
  const [quota, setQuota] = useState(null);
  const [quotaForm, setQuotaForm] = useState({ ai_enabled: true, daily_limit: 30, monthly_limit: 300, cost_per_call_inr: 0.20 });
  const [overrideForm, setOverrideForm] = useState({ user_id: '', daily_limit: '', monthly_limit: '' });
  const [cacheStats, setCacheStats] = useState(null);

  const clearCache = async () => {
    if (!window.confirm('Poori AI cache clear kar dein? Repeat try-ons ab full cost pe run honge.')) return;
    try { const r = await api.delete('/admin/trial-room/cache'); toast.success(`Cleared ${r.data.deleted} entries`); load(); }
    catch (e) { toast.error('Failed'); }
  };

  const load = useCallback(async () => {
    try {
      const [d, s, p, cfg, u, qt, ch] = await Promise.all([
        api.get('/admin/trial-room/dashboard'),
        api.get('/admin/trial-room/subscriptions?limit=100'),
        api.get('/trial-room/products'),
        api.get('/admin/trial-room/settings').catch(() => ({ data: { settings: { ai_model: 'gpt-4o-mini' }, available_models: {} } })),
        api.get('/admin/trial-room/usage').catch(() => ({ data: null })),
        api.get('/admin/trial-room/quota').catch(() => ({ data: null })),
        api.get('/admin/trial-room/cache').catch(() => ({ data: null })),
      ]);
      setDash(d.data);
      setSubs(s.data.subscriptions || []);
      setProducts(p.data.products || []);
      const sset = cfg.data.settings || {};
      setSettings({ ai_model: sset.ai_model || 'gpt-4o-mini', shop_name: sset.shop_name || 'Anant Trial Room', shop_logo_url: sset.shop_logo_url || '', watermark_enabled: sset.watermark_enabled !== false, admin_whatsapp: sset.admin_whatsapp || '+919579369830' });
      setWmForm({ shop_name: sset.shop_name || 'Anant Trial Room', shop_logo_url: sset.shop_logo_url || '', watermark_enabled: sset.watermark_enabled !== false });
      setAvailableModels(cfg.data.available_models || {});
      setUsage(u.data);
      if (qt.data) {
        setQuota(qt.data);
        setQuotaForm({
          ai_enabled: qt.data.settings.ai_enabled,
          daily_limit: qt.data.settings.daily_limit,
          monthly_limit: qt.data.settings.monthly_limit,
          cost_per_call_inr: qt.data.settings.cost_per_call_inr,
        });
      }
      setCacheStats(ch.data);
    } catch (e) { toast.error('Load failed'); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const saveQuota = async () => {
    setBusy(true);
    try {
      await api.post('/admin/trial-room/quota', quotaForm);
      toast.success('✅ Global AI quota updated');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  const setUserOverride = async () => {
    if (!overrideForm.user_id) return toast.error('User ID required');
    setBusy(true);
    try {
      await api.post('/admin/trial-room/quota/override', {
        user_id: overrideForm.user_id,
        daily_limit: overrideForm.daily_limit === '' ? null : Number(overrideForm.daily_limit),
        monthly_limit: overrideForm.monthly_limit === '' ? null : Number(overrideForm.monthly_limit),
      });
      toast.success('✅ Per-user override saved');
      setOverrideForm({ user_id: '', daily_limit: '', monthly_limit: '' });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  const removeOverride = async (user_id) => {
    if (!window.confirm(`Remove override for ${user_id}?`)) return;
    try {
      await api.post('/admin/trial-room/quota/override', { user_id, daily_limit: null, monthly_limit: null });
      toast.success('Override removed');
      load();
    } catch (e) { toast.error('Failed'); }
  };

  const saveWatermark = async () => {
    setBusy(true);
    try {
      await api.post('/admin/trial-room/settings', wmForm);
      toast.success('✅ Watermark settings saved');
      load();
    } catch (e) { toast.error('Save failed'); }
    finally { setBusy(false); }
  };

  const addProduct = async () => {
    if (!form.name.trim() || !form.image_url.trim()) return toast.error('Name aur Image URL zaroori hai');
    setBusy(true);
    try {
      await api.post('/admin/trial-room/products', {
        ...form,
        price: form.price ? Number(form.price) : null,
        tags: form.tags ? form.tags.split(',').map((t) => t.trim()).filter(Boolean) : [],
      });
      toast.success(`✅ ${form.name} added to ${form.category}`);
      setForm({ ...form, subcategory: '', name: '', image_url: '', price: '', retailer: '', tags: '' });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Add failed'); }
    finally { setBusy(false); }
  };

  const delProduct = async (pid, name) => {
    if (!window.confirm(`Delete "${name}"?`)) return;
    try { await api.delete(`/admin/trial-room/products/${pid}`); toast.success('Deleted'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  const filtered = products.filter((p) => (p.category || 'apparels') === activeCat);

  return (
    <div className="min-h-screen pt-24 pb-16 px-4 bg-gradient-to-br from-slate-950 via-cyan-950/40 to-slate-950 text-white" data-testid="trial-room-admin">
      <div className="max-w-7xl mx-auto space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-cyan-300 font-black">॥ AI Trial Room · Admin ॥</div>
            <h1 className="text-2xl md:text-3xl font-black bg-gradient-to-r from-cyan-300 via-fuchsia-300 to-amber-200 bg-clip-text text-transparent">🪞 Products Manager</h1>
          </div>
          <button onClick={load} className="px-3 py-1.5 rounded-lg bg-white/10 text-slate-200 flex items-center gap-1 text-xs"><RefreshCw className="w-3 h-3" /> Reload</button>
        </div>

        {/* KPIs */}
        {dash && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <KPI icon={Clock}    label="Trial Users"      val={fmt(dash.trial_users)} color="from-cyan-500/20 to-teal-500/20 border-cyan-400/30" />
            <KPI icon={Users}    label="Active Subscribers" val={fmt(dash.active_subscriptions)} color="from-emerald-500/20 to-green-500/20 border-emerald-400/30" />
            <KPI icon={Store}    label="Total Products"   val={fmt(products.length)} color="from-fuchsia-500/20 to-pink-500/20 border-fuchsia-400/30" />
            <KPI icon={DollarSign} label="Total Revenue AT" val={fmt(dash.total_revenue_at)} color="from-amber-500/20 to-orange-500/20 border-amber-400/30" />
          </div>
        )}

        {/* AI Model Selector */}
        <div className="rounded-2xl border-2 border-purple-400/40 bg-purple-500/5 p-4" data-testid="ai-model-panel">
          <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
            <div>
              <div className="text-sm font-black text-purple-300">🤖 AI Model (for Advice + Best Match)</div>
              <div className="text-[10px] text-slate-400">Cheaper model = higher profit margin per shop. All support vision.</div>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
            {Object.entries(availableModels).map(([key, m]) => (
              <button
                key={key}
                onClick={async () => {
                  try {
                    await api.post('/admin/trial-room/settings', { ai_model: key });
                    setSettings({ ...settings, ai_model: key });
                    toast.success(`✅ AI model set to: ${m.label}`);
                  } catch { toast.error('Failed to save'); }
                }}
                data-testid={`ai-model-${key}`}
                className={`p-2.5 rounded-xl text-left transition-all border-2 ${settings.ai_model === key ? 'border-purple-400 bg-purple-500/20 ring-2 ring-purple-400/40' : 'border-white/10 bg-white/5 hover:border-purple-400/50 hover:bg-white/10'}`}>
                <div className="text-[11px] font-black text-white">{m.label}</div>
                <div className="text-[9px] text-slate-400 mt-0.5 uppercase">{m.provider}</div>
                {settings.ai_model === key && <div className="text-[9px] text-purple-300 font-bold mt-1 uppercase tracking-widest">✓ ACTIVE</div>}
              </button>
            ))}
          </div>
          <div className="mt-2 text-[10px] text-purple-200/80 italic">
            💡 Recommended for scale: <b>Gemini 3.5 Flash</b> (4x cheaper than GPT-4o-mini, similar quality for style advice)
          </div>
        </div>

        {/* 📊 USAGE TRACKER */}
        <div className="rounded-2xl border-2 border-emerald-400/40 bg-emerald-500/5 p-4" data-testid="usage-tracker-panel">
          <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
            <div>
              <div className="text-sm font-black text-emerald-300 flex items-center gap-1.5"><TrendingUp className="w-4 h-4" /> Usage Tracker · Trial Analytics</div>
              <div className="text-[10px] text-slate-400">Live counter — kaunse dresses sabse zyada try ho rahe hain</div>
            </div>
          </div>
          {usage ? (
            <>
              <div className="grid grid-cols-3 gap-2 mb-3">
                <MiniStat label="Today" val={fmt(usage.today)} accent="text-emerald-300" />
                <MiniStat label="This Week" val={fmt(usage.week)} accent="text-cyan-300" />
                <MiniStat label="All-Time" val={fmt(usage.total)} accent="text-amber-300" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {/* Top products */}
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-black mb-1.5">🔥 Most-Tried Apparels (30d)</div>
                  {(usage.top_products || []).length === 0 ? (
                    <div className="text-[10px] text-slate-500 italic">No data yet — try-ons will appear here.</div>
                  ) : (
                    <div className="space-y-1">
                      {usage.top_products.map((p, i) => (
                        <div key={p.product_id || i} className="flex items-center justify-between gap-2 rounded bg-emerald-500/10 px-2 py-1">
                          <div className="text-[11px] text-white truncate"><span className="text-emerald-300 font-black mr-1">#{i + 1}</span> {p.product_name || '(unknown)'}</div>
                          <div className="text-[10px] font-mono text-amber-300">{p.trials}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                {/* Flow + character breakdown */}
                <div className="space-y-2">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-black mb-1">🎯 Character Preference (30d)</div>
                    <div className="flex gap-1.5 flex-wrap">
                      {(usage.character_stats || []).map((c) => (
                        <div key={c.character} className="text-[10px] px-2 py-1 rounded-full bg-fuchsia-500/20 border border-fuchsia-400/40 text-fuchsia-200 font-bold uppercase">
                          {c.character} · {c.count}
                        </div>
                      ))}
                      {(usage.character_stats || []).length === 0 && <div className="text-[10px] text-slate-500 italic">No data</div>}
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-black mb-1">📥 Flow Split (7d)</div>
                    <div className="flex gap-1.5 flex-wrap">
                      {(usage.flow_stats || []).map((f) => (
                        <div key={f.flow} className="text-[10px] px-2 py-1 rounded-full bg-cyan-500/20 border border-cyan-400/40 text-cyan-200 font-bold uppercase">
                          {f.flow.replace('_', ' ')} · {f.count}
                        </div>
                      ))}
                      {(usage.flow_stats || []).length === 0 && <div className="text-[10px] text-slate-500 italic">No data</div>}
                    </div>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="text-[11px] text-slate-500 italic">Loading usage data…</div>
          )}
        </div>

        {/* ⚡ AI QUOTA CONTROL (admin sets limits, sees costs) */}
        <div className="rounded-2xl border-2 border-red-400/40 bg-red-500/5 p-4" data-testid="quota-panel">
          <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
            <div>
              <div className="text-sm font-black text-red-300 flex items-center gap-1.5"><Zap className="w-4 h-4" /> AI Usage Quota & Cost Control</div>
              <div className="text-[10px] text-slate-400">Har user pe daily/monthly image-generation limit — cost control ke liye</div>
            </div>
            <label className="flex items-center gap-1.5 text-[11px] text-slate-300 cursor-pointer">
              <input
                type="checkbox"
                checked={quotaForm.ai_enabled}
                onChange={(e) => setQuotaForm({ ...quotaForm, ai_enabled: e.target.checked })}
                data-testid="quota-ai-enabled"
                className="accent-red-500"
              />
              AI Generation Enabled (global)
            </label>
          </div>
          {quota && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
              <MiniStat label="Today (all users)" val={fmt(quota.today_total_calls)} accent="text-emerald-300" />
              <MiniStat label="Month (all users)" val={fmt(quota.month_total_calls)} accent="text-cyan-300" />
              <MiniStat label="Est. Month Cost" val={`₹${fmt(quota.est_month_cost_inr)}`} accent="text-amber-300" />
              <MiniStat label="Cost / Call" val={`₹${quota.settings.cost_per_call_inr}`} accent="text-fuchsia-300" />
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs mb-2">
            <label className="flex flex-col gap-0.5">
              <span className="text-[10px] text-slate-400 uppercase font-black">Daily Limit / User</span>
              <input
                type="number" min="0"
                value={quotaForm.daily_limit}
                onChange={(e) => setQuotaForm({ ...quotaForm, daily_limit: Number(e.target.value) })}
                data-testid="quota-daily"
                className="bg-white/5 border border-white/10 rounded px-2 py-1.5"
              />
            </label>
            <label className="flex flex-col gap-0.5">
              <span className="text-[10px] text-slate-400 uppercase font-black">Monthly Limit / User</span>
              <input
                type="number" min="0"
                value={quotaForm.monthly_limit}
                onChange={(e) => setQuotaForm({ ...quotaForm, monthly_limit: Number(e.target.value) })}
                data-testid="quota-monthly"
                className="bg-white/5 border border-white/10 rounded px-2 py-1.5"
              />
            </label>
            <label className="flex flex-col gap-0.5">
              <span className="text-[10px] text-slate-400 uppercase font-black">Cost per Call (INR)</span>
              <input
                type="number" step="0.01" min="0"
                value={quotaForm.cost_per_call_inr}
                onChange={(e) => setQuotaForm({ ...quotaForm, cost_per_call_inr: Number(e.target.value) })}
                data-testid="quota-cost"
                className="bg-white/5 border border-white/10 rounded px-2 py-1.5"
              />
            </label>
          </div>
          <button
            onClick={saveQuota}
            disabled={busy}
            data-testid="quota-save-btn"
            className="px-3 py-1.5 rounded-lg bg-gradient-to-r from-red-500 to-orange-500 text-white text-xs font-black flex items-center gap-1 hover:brightness-110 disabled:opacity-50"
          >
            {busy ? 'Saving…' : <><Save className="w-3 h-3" /> Save Global Quota</>}
          </button>

          {/* Per-user override form */}
          <div className="mt-4 pt-3 border-t border-white/10">
            <div className="text-[10px] uppercase tracking-widest text-red-300 font-black mb-2">Per-User Override (VIP shops)</div>
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 text-xs">
              <input placeholder="User ID" value={overrideForm.user_id} onChange={(e) => setOverrideForm({ ...overrideForm, user_id: e.target.value })} data-testid="override-uid" className="bg-white/5 border border-white/10 rounded px-2 py-1.5" />
              <input type="number" placeholder="Daily limit" value={overrideForm.daily_limit} onChange={(e) => setOverrideForm({ ...overrideForm, daily_limit: e.target.value })} data-testid="override-daily" className="bg-white/5 border border-white/10 rounded px-2 py-1.5" />
              <input type="number" placeholder="Monthly limit" value={overrideForm.monthly_limit} onChange={(e) => setOverrideForm({ ...overrideForm, monthly_limit: e.target.value })} data-testid="override-monthly" className="bg-white/5 border border-white/10 rounded px-2 py-1.5" />
              <button onClick={setUserOverride} disabled={busy} data-testid="override-save-btn" className="px-3 py-1.5 rounded-lg bg-cyan-500 text-white text-xs font-black disabled:opacity-50">+ Set Override</button>
            </div>
            {quota && Object.keys(quota.settings.per_user_overrides || {}).length > 0 && (
              <div className="mt-2 space-y-1">
                {Object.entries(quota.settings.per_user_overrides).map(([uid, ov]) => (
                  <div key={uid} className="flex items-center gap-2 rounded bg-cyan-500/10 border border-cyan-400/30 px-2 py-1 text-[10px]">
                    <span className="font-mono text-cyan-300 truncate flex-1">{uid}</span>
                    <span className="text-slate-300">D:{ov.daily_limit || '—'} · M:{ov.monthly_limit || '—'}</span>
                    <button onClick={() => removeOverride(uid)} className="text-red-300 hover:text-red-200">✕</button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Top users list */}
          {quota && quota.top_users_this_month.length > 0 && (
            <div className="mt-3 pt-3 border-t border-white/10">
              <div className="text-[10px] uppercase tracking-widest text-red-300 font-black mb-1">🔥 Top 10 Users this Month</div>
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-1">
                {quota.top_users_this_month.map((u, i) => (
                  <div key={u.user_id} className="rounded bg-red-500/10 border border-red-400/30 px-2 py-1 text-[10px]">
                    <div className="font-black text-red-300">#{i + 1} · {u.calls} calls</div>
                    <div className="text-white truncate">{u.username || u.user_id.slice(0, 12)}</div>
                    <div className="text-slate-400">₹{(u.calls * quota.settings.cost_per_call_inr).toFixed(2)}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* 💾 Cache stats — money saved */}
          {cacheStats && (
            <div className="mt-3 pt-3 border-t border-white/10" data-testid="cache-panel">
              <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-black flex items-center gap-1">💾 AI Cache · ₹0 Repeat Try-Ons</div>
                  <div className="text-[9px] text-slate-400">Identical customer+apparel combo → instant response, no Gemini call</div>
                </div>
                <button onClick={clearCache} data-testid="cache-clear-btn" className="text-[10px] px-2 py-1 rounded bg-red-500/70 text-white font-black">Clear Cache</button>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <MiniStat label="Cached Entries" val={fmt(cacheStats.total_cached_entries)} accent="text-emerald-300" />
                <MiniStat label="Cache Hits" val={fmt(cacheStats.total_cache_hits)} accent="text-cyan-300" />
                <MiniStat label="₹ Saved" val={`₹${fmt(cacheStats.estimated_inr_saved)}`} accent="text-amber-300" />
              </div>
            </div>
          )}
        </div>

        {/* 💧 WATERMARK / BRANDING */}
        <div className="rounded-2xl border-2 border-amber-400/40 bg-amber-500/5 p-4" data-testid="watermark-panel">
          <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
            <div>
              <div className="text-sm font-black text-amber-300 flex items-center gap-1.5"><ImageIcon className="w-4 h-4" /> Watermark & Branding</div>
              <div className="text-[10px] text-slate-400">Har download aur WhatsApp share pe shop ka naam + logo automatically overlay hoga</div>
            </div>
            <label className="flex items-center gap-1.5 text-[11px] text-slate-300 cursor-pointer">
              <input
                type="checkbox"
                checked={wmForm.watermark_enabled}
                onChange={(e) => setWmForm({ ...wmForm, watermark_enabled: e.target.checked })}
                data-testid="wm-enabled-toggle"
                className="accent-amber-400"
              />
              Enable Watermark
            </label>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
            <input
              placeholder="Shop / Brand Name (e.g. Anil's Boutique)"
              value={wmForm.shop_name}
              onChange={(e) => setWmForm({ ...wmForm, shop_name: e.target.value })}
              data-testid="wm-shop-name"
              className="bg-white/5 border border-white/10 rounded px-2 py-1.5"
              maxLength={80}
            />
            <input
              placeholder="Logo URL (png/jpg, transparent bg best)"
              value={wmForm.shop_logo_url}
              onChange={(e) => setWmForm({ ...wmForm, shop_logo_url: e.target.value })}
              data-testid="wm-logo-url"
              className="bg-white/5 border border-white/10 rounded px-2 py-1.5"
            />
          </div>
          <div className="flex items-center gap-3 mt-2 flex-wrap">
            <button
              onClick={saveWatermark}
              disabled={busy}
              data-testid="wm-save-btn"
              className="px-3 py-1.5 rounded-lg bg-gradient-to-r from-amber-500 to-orange-500 text-black text-xs font-black flex items-center gap-1 hover:brightness-110 disabled:opacity-50"
            >
              {busy ? 'Saving…' : '💾 Save Watermark'}
            </button>
            {wmForm.shop_logo_url && (
              <div className="flex items-center gap-1.5">
                <span className="text-[10px] text-slate-400">Preview:</span>
                <img src={wmForm.shop_logo_url} alt="logo" className="w-8 h-8 rounded object-contain bg-black/40 border border-white/10" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
              </div>
            )}
          </div>
        </div>

        {/* 📞 CUSTOM PACK WHATSAPP */}
        <div className="rounded-2xl border-2 border-emerald-400/40 bg-emerald-500/5 p-4" data-testid="admin-whatsapp-panel">
          <div className="text-sm font-black text-emerald-300 flex items-center gap-1.5 mb-1">📞 Custom Pack WhatsApp Number</div>
          <div className="text-[10px] text-slate-400 mb-2">Trial Room &ldquo;Custom Pack&rdquo; card ke saath ye number attach hoga. Format: <code className="text-emerald-200">+91XXXXXXXXXX</code></div>
          <div className="flex gap-2 flex-wrap">
            <input
              placeholder="+919579369830"
              value={settings.admin_whatsapp || ''}
              onChange={(e) => setSettings({ ...settings, admin_whatsapp: e.target.value })}
              data-testid="admin-whatsapp-input"
              className="flex-1 min-w-[220px] bg-white/5 border border-white/10 rounded px-2 py-1.5 text-xs font-mono"
              maxLength={20}
            />
            <button
              onClick={async () => {
                const val = (settings.admin_whatsapp || '').trim();
                if (!/^\+?\d{8,15}$/.test(val.replace(/\s+/g, ''))) { toast.error('Invalid WhatsApp number'); return; }
                try {
                  await api.post('/admin/trial-room/settings', { admin_whatsapp: val });
                  toast.success('✅ Custom Pack WhatsApp updated');
                } catch (e) { toast.error('Save failed: ' + (e?.response?.data?.detail || 'unknown')); }
              }}
              data-testid="admin-whatsapp-save"
              className="px-3 py-1.5 rounded-lg bg-gradient-to-r from-emerald-500 to-teal-500 text-black text-xs font-black">
              💾 Save
            </button>
          </div>
        </div>

        {/* Category tabs */}
        <div className="flex gap-2 flex-wrap">
          {CATEGORIES.map((c) => (
            <button
              key={c.id}
              onClick={() => setActiveCat(c.id)}
              data-testid={`admin-cat-${c.id}`}
              className={`px-3 py-2 rounded-lg text-xs font-black border-2 transition ${activeCat === c.id ? `bg-gradient-to-r ${c.color} text-white border-white/40` : 'bg-white/5 text-slate-300 border-white/10 hover:bg-white/10'}`}>
              {c.label} <span className="opacity-75">({products.filter((p) => p.category === c.id).length})</span>
            </button>
          ))}
        </div>

        {/* Add Product Form */}
        <div className="rounded-2xl border-2 border-cyan-400/40 bg-cyan-500/5 p-4">
          <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
            <div className="text-sm font-black text-cyan-300">➕ Add New Product</div>
            {/* CSV Bulk Import */}
            <div className="flex items-center gap-1.5 flex-wrap">
              <a
                href={`data:text/csv;charset=utf-8,${encodeURIComponent('category,name,image_url,subcategory,retailer,price,tags\napparels,Silk Kurta,https://example.com/kurta.jpg,kurta,Sample Store,1499,"traditional,silk"\njewelry,Gold Necklace,https://example.com/necklace.jpg,necklace,Sample Jewellers,25000,"gold,wedding"\nsalon,Long Layers,https://example.com/hair.jpg,haircut,Style Studio,,layered')}`}
                download="trial-room-products-template.csv"
                data-testid="csv-download-template"
                className="text-[10px] px-2 py-1 rounded bg-white/10 text-slate-200 hover:bg-white/20 font-bold">
                📥 Download CSV Template
              </a>
              <label data-testid="csv-import-label" className="text-[10px] px-2 py-1 rounded bg-gradient-to-r from-fuchsia-500 to-pink-500 text-white cursor-pointer font-black hover:brightness-110">
                📤 Bulk CSV Import
                <input
                  type="file"
                  accept=".csv,text/csv"
                  data-testid="csv-import-input"
                  className="hidden"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    const text = await file.text();
                    // Parse CSV — handles quoted fields with commas inside
                    const parseLine = (line) => {
                      const out = []; let cur = ''; let inQ = false;
                      for (let i = 0; i < line.length; i++) {
                        const ch = line[i];
                        if (ch === '"') { if (inQ && line[i + 1] === '"') { cur += '"'; i++; } else inQ = !inQ; }
                        else if (ch === ',' && !inQ) { out.push(cur); cur = ''; }
                        else cur += ch;
                      }
                      out.push(cur);
                      return out.map((s) => s.trim());
                    };
                    const lines = text.split(/\r?\n/).filter((l) => l.trim());
                    if (lines.length < 2) { toast.error('CSV empty or missing header'); e.target.value = ''; return; }
                    const headers = parseLine(lines[0]).map((h) => h.toLowerCase());
                    const requiredIdx = { category: headers.indexOf('category'), name: headers.indexOf('name'), image_url: headers.indexOf('image_url') };
                    if (requiredIdx.category < 0 || requiredIdx.name < 0 || requiredIdx.image_url < 0) {
                      toast.error('CSV headers must include: category, name, image_url'); e.target.value = ''; return;
                    }
                    const rows = lines.slice(1).map(parseLine).filter((r) => r[requiredIdx.name] && r[requiredIdx.image_url]);
                    if (!window.confirm(`Import ${rows.length} products from "${file.name}"?\n\nCategory column values must be one of: apparels / jewelry / salon`)) { e.target.value = ''; return; }
                    setBusy(true);
                    let ok = 0, fail = 0; const errs = [];
                    for (let i = 0; i < rows.length; i++) {
                      const r = rows[i];
                      const get = (k) => { const idx = headers.indexOf(k); return idx >= 0 ? (r[idx] || '') : ''; };
                      const cat = get('category').toLowerCase();
                      if (!['apparels', 'jewelry', 'salon'].includes(cat)) { fail++; errs.push(`Row ${i + 2}: invalid category "${cat}"`); continue; }
                      try {
                        await api.post('/admin/trial-room/products', {
                          category: cat,
                          name: get('name'),
                          image_url: get('image_url'),
                          subcategory: get('subcategory') || null,
                          retailer: get('retailer') || null,
                          price: get('price') ? Number(get('price')) : null,
                          tags: get('tags') ? get('tags').split(',').map((t) => t.trim()).filter(Boolean) : [],
                          sort_order: 100,
                        });
                        ok++;
                      } catch (err) { fail++; errs.push(`Row ${i + 2}: ${err.response?.data?.detail || 'failed'}`); }
                    }
                    setBusy(false);
                    e.target.value = '';
                    if (ok > 0) toast.success(`✅ Imported ${ok} products` + (fail ? ` · ${fail} failed` : ''));
                    if (fail > 0 && errs.length) toast.error(`❌ ${fail} failed:\n${errs.slice(0, 3).join('\n')}${errs.length > 3 ? `\n...+${errs.length - 3} more` : ''}`);
                    load();
                  }}
                />
              </label>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 text-xs">
            <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="prod-category" className="bg-slate-900 border border-white/20 rounded px-2 py-1.5">
              {CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
            </select>
            <input placeholder="Product Name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="prod-name" className="bg-white/5 border border-white/10 rounded px-2 py-1.5" />
            <input placeholder="Image URL * (jpg/png/webp)" value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} data-testid="prod-image" className="bg-white/5 border border-white/10 rounded px-2 py-1.5" />
            <input placeholder="Subcategory (e.g. kurta, necklace)" value={form.subcategory} onChange={(e) => setForm({ ...form, subcategory: e.target.value })} data-testid="prod-subcat" className="bg-white/5 border border-white/10 rounded px-2 py-1.5" />
            <input placeholder="Retailer Name (e.g. XYZ Store)" value={form.retailer} onChange={(e) => setForm({ ...form, retailer: e.target.value })} data-testid="prod-retailer" className="bg-white/5 border border-white/10 rounded px-2 py-1.5" />
            <input type="number" placeholder="Price (INR, optional)" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="prod-price" className="bg-white/5 border border-white/10 rounded px-2 py-1.5" />
            <input placeholder="Tags (comma-separated)" value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} data-testid="prod-tags" className="bg-white/5 border border-white/10 rounded px-2 py-1.5 sm:col-span-2" />
            <input type="number" placeholder="Sort Order (100)" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: Number(e.target.value) || 100 })} data-testid="prod-sort" className="bg-white/5 border border-white/10 rounded px-2 py-1.5" />
          </div>
          <button onClick={addProduct} disabled={busy} data-testid="prod-add-btn" className="mt-2 w-full py-2 rounded-lg bg-gradient-to-r from-cyan-500 to-fuchsia-500 text-white font-black text-sm flex items-center justify-center gap-1 disabled:opacity-50">
            <Plus className="w-4 h-4" /> {busy ? 'Adding...' : `Add to ${CATEGORIES.find((c) => c.id === form.category)?.label}`}
          </button>
        </div>

        {/* Products Grid */}
        <div className="rounded-2xl border border-white/10 bg-black/30 p-4">
          <div className="text-sm font-bold mb-3 text-slate-200">
            {CATEGORIES.find((c) => c.id === activeCat)?.label} · {filtered.length} products
          </div>
          {filtered.length === 0 ? (
            <div className="text-center py-8 text-sm text-slate-500 italic">Koi product nahi hai. Upar se add karein.</div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
              {filtered.map((p) => (
                <div key={p.id} className="rounded-xl border border-white/10 bg-slate-900/50 overflow-hidden group" data-testid={`prod-card-${p.id}`}>
                  <div className="relative aspect-square bg-black/40">
                    <img src={p.image_url} alt={p.name} loading="lazy" onError={(e) => { e.currentTarget.style.opacity = '0.3'; }} className="w-full h-full object-cover" />
                    <button onClick={() => delProduct(p.id, p.name)} data-testid={`prod-del-${p.id}`} className="absolute top-1 right-1 p-1 rounded bg-red-500/70 hover:bg-red-500 text-white opacity-0 group-hover:opacity-100 transition"><Trash2 className="w-3 h-3" /></button>
                  </div>
                  <div className="p-2">
                    <div className="text-xs font-bold text-white truncate">{p.name}</div>
                    {p.subcategory && <div className="text-[9px] text-cyan-300 uppercase tracking-wider">{p.subcategory}</div>}
                    {p.retailer && <div className="text-[10px] text-slate-400 truncate">🏬 {p.retailer}</div>}
                    {p.price && <div className="text-[10px] text-amber-300 font-mono">₹{p.price}</div>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Subscriptions List */}
        <div className="rounded-2xl border border-emerald-400/30 bg-emerald-500/5 p-4">
          <div className="text-sm font-bold text-emerald-300 mb-2">💎 Recent Subscriptions ({subs.length})</div>
          {subs.length === 0 ? (
            <div className="text-xs text-slate-500 italic text-center py-4">Koi subscription abhi tak nahi.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="text-slate-400 uppercase text-[10px]">
                  <tr><th className="text-left px-2 py-1">User</th><th className="text-left px-2 py-1">Pack</th><th className="text-right px-2 py-1">Amount</th><th className="text-left px-2 py-1">Expires</th><th className="text-center px-2 py-1">Status</th></tr>
                </thead>
                <tbody>
                  {subs.map((s) => {
                    const active = new Date(s.expires_at) > new Date() && s.status === 'active';
                    return (
                      <tr key={s.id} className="border-t border-white/5" data-testid={`sub-row-${s.id}`}>
                        <td className="px-2 py-1 text-white font-bold">{s.full_name || s.username}</td>
                        <td className="px-2 py-1 text-fuchsia-300">{s.package_label}</td>
                        <td className="px-2 py-1 text-right font-mono text-amber-300">{fmt(s.amount)} AT</td>
                        <td className="px-2 py-1 text-slate-400">{new Date(s.expires_at).toLocaleDateString('en-IN')}</td>
                        <td className="px-2 py-1 text-center">
                          {active ? <span className="text-[9px] px-1.5 py-0.5 rounded bg-emerald-500/30 text-emerald-200 font-black">✓ ACTIVE</span>
                                  : <span className="text-[9px] px-1.5 py-0.5 rounded bg-red-500/30 text-red-200 font-black">EXPIRED</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* 🎯 Assign Products/Items to Specific Users (Admin-only private uploads) */}
          <AssignProductsToUsersPanel />

          {/* 📊 Batch Analytics — click / VTON try / 👍👎 per share-link */}
          <BatchAnalyticsPanel />
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Admin — Upload private products/items to specific user(s)
// ─────────────────────────────────────────────────────────────
function AssignProductsToUsersPanel() {
  const [searchQ, setSearchQ] = useState('');
  const [results, setResults] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]); // [{id, name, username, email}]
  const [items, setItems] = useState([
    { category: 'apparels', name: '', image_url: '', subcategory: '', price: '', retailer: '' },
  ]);
  const [busy, setBusy] = useState(false);
  const [existing, setExisting] = useState([]);
  const [viewUid, setViewUid] = useState('');
  const [lastBatch, setLastBatch] = useState(null);
  const [copiedLink, setCopiedLink] = useState(false);

  const search = useCallback(async () => {
    if (searchQ.trim().length < 2) { setResults([]); return; }
    try {
      const r = await api.get('/admin/trial-room/user-search', { params: { q: searchQ.trim(), limit: 10 } });
      setResults(r.data.users || []);
    } catch (_) { /* silent */ }
  }, [searchQ]);
  useEffect(() => { const t = setTimeout(search, 350); return () => clearTimeout(t); }, [searchQ, search]);

  const addUser = (u) => {
    if (selectedUsers.some((x) => x.id === u.id)) return;
    setSelectedUsers([...selectedUsers, u]);
    setSearchQ(''); setResults([]);
  };
  const removeUser = (uid) => setSelectedUsers(selectedUsers.filter((u) => u.id !== uid));

  const updateItem = (i, key, val) => {
    const next = [...items]; next[i] = { ...next[i], [key]: val }; setItems(next);
  };
  const addItemRow = () => setItems([...items, { category: 'apparels', name: '', image_url: '', subcategory: '', price: '', retailer: '' }]);
  const removeItemRow = (i) => setItems(items.filter((_, idx) => idx !== i));

  // Base64 upload → data URL stored directly in image_url
  const uploadImageForItem = async (i, file) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) { toast.error('Image > 8 MB'); return; }
    const reader = new FileReader();
    reader.onload = (ev) => updateItem(i, 'image_url', ev.target.result);
    reader.readAsDataURL(file);
  };

  const submit = async () => {
    if (selectedUsers.length === 0) { toast.error('Select at least 1 user'); return; }
    const valid = items.filter((it) => it.name.trim() && it.image_url.trim());
    if (valid.length === 0) { toast.error('Add at least 1 item (name + image required)'); return; }
    setBusy(true);
    try {
      const payload = {
        user_ids: selectedUsers.map((u) => u.id),
        items: valid.map((it) => ({
          category: it.category, subcategory: it.subcategory || null,
          name: it.name.trim(), image_url: it.image_url,
          price: it.price ? Number(it.price) : null,
          retailer: it.retailer || null, tags: null, sort_order: 100,
        })),
      };
      const r = await api.post('/admin/trial-room/products/bulk-assign-to-users', payload);
      toast.success(`✅ ${r.data.created_count} items assigned to ${r.data.user_count} user(s)`);
      // 🔗 Build share link — user click karte hi seedha unlock toast + private catalog
      const shareLink = `${window.location.origin}/trial-room?assigned_batch=${r.data.batch_id}`;
      setLastBatch({
        batch_id: r.data.batch_id,
        count: r.data.created_count,
        user_count: r.data.user_count,
        user_names: selectedUsers.map((u) => u.name),
        link: shareLink,
      });
      setCopiedLink(false);
      setItems([{ category: 'apparels', name: '', image_url: '', subcategory: '', price: '', retailer: '' }]);
      setSelectedUsers([]);
      if (viewUid) loadExisting(viewUid);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to assign');
    }
    setBusy(false);
  };

  const loadExisting = async (uid) => {
    setViewUid(uid);
    if (!uid) { setExisting([]); return; }
    try {
      const r = await api.get('/admin/trial-room/user-assigned-products', { params: { user_id: uid } });
      setExisting(r.data.products || []);
    } catch (_) { setExisting([]); }
  };

  const deleteAssigned = async (pid) => {
    if (!window.confirm('Delete this assigned item?')) return;
    try {
      await api.delete(`/admin/trial-room/products/${pid}`);
      toast.success('Deleted');
      if (viewUid) loadExisting(viewUid);
    } catch (_) { toast.error('Delete failed'); }
  };

  return (
    <div className="rounded-2xl border-2 border-cyan-400/50 bg-gradient-to-br from-cyan-950/30 to-slate-900 p-4 mt-4" data-testid="assign-products-panel">
      <div className="mb-3">
        <div className="text-[10px] uppercase tracking-widest text-cyan-300 font-black">🎯 PRIVATE UPLOADS · PER-USER</div>
        <h3 className="text-lg font-black">Assign Products/Items to Specific Users</h3>
        <p className="text-xs text-slate-400">Upload apparels · jewelry · salon items · mixed — only these selected users will see them in their Trial Room.</p>
      </div>

      {/* User selection */}
      <div className="rounded-lg bg-black/30 border border-white/10 p-3 mb-3">
        <div className="text-[10px] uppercase text-cyan-300 font-black mb-1.5">1. Select User(s)</div>
        <input
          type="text" value={searchQ} onChange={(e) => setSearchQ(e.target.value)}
          placeholder="Search by name / username / email (min 2 chars)"
          className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm text-white"
          data-testid="assign-user-search"
        />
        {results.length > 0 && (
          <div className="mt-2 max-h-40 overflow-y-auto space-y-1">
            {results.map((u) => (
              <button key={u.id} onClick={() => addUser(u)} data-testid={`assign-user-result-${u.id.slice(0,8)}`}
                className="w-full text-left px-2 py-1.5 rounded bg-white/5 hover:bg-cyan-500/20 text-xs">
                <span className="font-bold text-cyan-200">{u.name}</span> <span className="text-slate-400">· @{u.username} · {u.email}</span>
              </button>
            ))}
          </div>
        )}
        {selectedUsers.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1.5" data-testid="assign-selected-users">
            {selectedUsers.map((u) => (
              <span key={u.id} className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-cyan-500/20 border border-cyan-400/40 text-[11px] text-cyan-100">
                {u.name} · @{u.username}
                <button onClick={() => removeUser(u.id)} className="ml-1 text-rose-300 hover:text-rose-100" data-testid={`assign-remove-user-${u.id.slice(0,8)}`}>×</button>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Items list */}
      <div className="rounded-lg bg-black/30 border border-white/10 p-3 mb-3">
        <div className="text-[10px] uppercase text-cyan-300 font-black mb-2">2. Add Items (mix categories freely)</div>
        <div className="space-y-2">
          {items.map((it, i) => (
            <div key={i} className="rounded-lg border border-white/10 bg-white/5 p-2.5" data-testid={`assign-item-row-${i}`}>
              <div className="grid grid-cols-1 sm:grid-cols-6 gap-2">
                <select value={it.category} onChange={(e) => updateItem(i, 'category', e.target.value)}
                  className="sm:col-span-1 bg-slate-950 border border-white/10 rounded px-2 py-1.5 text-xs"
                  data-testid={`assign-item-cat-${i}`}>
                  {CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
                </select>
                <input type="text" placeholder="Item name *" value={it.name}
                  onChange={(e) => updateItem(i, 'name', e.target.value)}
                  className="sm:col-span-2 bg-slate-950 border border-white/10 rounded px-2 py-1.5 text-xs" data-testid={`assign-item-name-${i}`} />
                <input type="text" placeholder="Subcategory (optional)" value={it.subcategory}
                  onChange={(e) => updateItem(i, 'subcategory', e.target.value)}
                  className="sm:col-span-1 bg-slate-950 border border-white/10 rounded px-2 py-1.5 text-xs" />
                <input type="number" placeholder="Price (₹)" value={it.price}
                  onChange={(e) => updateItem(i, 'price', e.target.value)}
                  className="sm:col-span-1 bg-slate-950 border border-white/10 rounded px-2 py-1.5 text-xs" />
                <button onClick={() => removeItemRow(i)} disabled={items.length === 1}
                  className="sm:col-span-1 px-2 py-1.5 rounded bg-rose-500/20 border border-rose-500/40 text-rose-300 text-xs disabled:opacity-30"
                  data-testid={`assign-item-remove-${i}`}>Remove</button>
              </div>
              <div className="mt-2 grid grid-cols-1 sm:grid-cols-3 gap-2 items-center">
                <input type="text" placeholder="Image URL (paste)" value={it.image_url.startsWith('data:') ? '(uploaded)' : it.image_url}
                  onChange={(e) => updateItem(i, 'image_url', e.target.value)}
                  className="sm:col-span-2 bg-slate-950 border border-white/10 rounded px-2 py-1.5 text-xs" data-testid={`assign-item-url-${i}`} />
                <label className="text-[10px] px-2 py-1.5 rounded bg-cyan-500/10 border border-cyan-500/40 text-cyan-200 cursor-pointer text-center">
                  📸 Upload
                  <input type="file" accept="image/*" className="hidden"
                    onChange={(e) => uploadImageForItem(i, e.target.files?.[0])} data-testid={`assign-item-file-${i}`} />
                </label>
              </div>
              {it.image_url && (
                <img src={it.image_url} alt="preview" className="mt-2 w-16 h-16 object-cover rounded border border-white/10" />
              )}
            </div>
          ))}
        </div>
        <button onClick={addItemRow} className="mt-2 text-xs text-cyan-300 hover:text-cyan-100" data-testid="assign-item-add-row">+ Add another item</button>
      </div>

      <button onClick={submit} disabled={busy}
        className="w-full py-2.5 rounded-lg bg-gradient-to-r from-cyan-500 to-fuchsia-500 text-white font-black text-sm disabled:opacity-50"
        data-testid="assign-submit">
        {busy ? 'Assigning…' : `🎁 Assign to ${selectedUsers.length} user(s)`}
      </button>

      {/* 🔗 Share Link — shown after successful assign */}
      {lastBatch && (
        <div className="mt-3 rounded-xl border-2 border-emerald-400/50 bg-gradient-to-br from-emerald-950/40 to-slate-900 p-3" data-testid="assign-share-link-panel">
          <div className="flex items-start justify-between gap-2 mb-2 flex-wrap">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-black">🔗 SHAREABLE UNLOCK LINK</div>
              <div className="text-sm font-black text-emerald-100">{lastBatch.count} items → {lastBatch.user_count} user(s)</div>
              <div className="text-[10px] text-slate-400 truncate max-w-xs">{lastBatch.user_names.slice(0, 3).join(', ')}{lastBatch.user_names.length > 3 ? '…' : ''}</div>
            </div>
            <button onClick={() => setLastBatch(null)} className="text-slate-400 hover:text-white text-xs" data-testid="assign-share-close">✕</button>
          </div>
          <div className="flex items-center gap-2 bg-black/40 rounded-lg p-2 mb-2">
            <input readOnly value={lastBatch.link}
              className="flex-1 bg-transparent text-[11px] font-mono text-emerald-200 outline-none"
              data-testid="assign-share-link-input"
              onFocus={(e) => e.target.select()} />
            <button
              onClick={() => {
                navigator.clipboard.writeText(lastBatch.link);
                setCopiedLink(true);
                toast.success('Link copied — WhatsApp pe paste kar do!');
                setTimeout(() => setCopiedLink(false), 2000);
              }}
              className="px-3 py-1.5 rounded bg-emerald-500 text-black text-xs font-black"
              data-testid="assign-share-copy">
              {copiedLink ? '✓ Copied' : '📋 Copy'}
            </button>
          </div>
          <a
            href={`https://wa.me/?text=${encodeURIComponent(`🎁 Aapke liye Anant Trial Room mein ${lastBatch.count} naya item unlock hua hai! Yahaan try karo: ${lastBatch.link}`)}`}
            target="_blank" rel="noopener noreferrer"
            className="block w-full text-center py-2 rounded bg-green-600 hover:bg-green-500 text-white text-xs font-black"
            data-testid="assign-share-whatsapp">
            📱 Share on WhatsApp
          </a>
          <div className="text-[10px] text-slate-400 mt-1.5">User link kholte hi seedha private items dikh jayenge with &quot;🎁 Unlock&quot; toast.</div>
        </div>
      )}

      {/* View existing assigned */}
      <div className="mt-4 pt-3 border-t border-white/10">
        <div className="text-[10px] uppercase text-cyan-300 font-black mb-1.5">3. View / Delete Existing Assigned Items</div>
        <div className="flex gap-2 mb-2">
          <input type="text" placeholder="Paste user_id to view their private items"
            value={viewUid} onChange={(e) => setViewUid(e.target.value)}
            className="flex-1 bg-slate-950 border border-white/10 rounded px-2 py-1.5 text-xs"
            data-testid="assign-view-uid" />
          <button onClick={() => loadExisting(viewUid.trim())}
            className="px-3 py-1.5 rounded bg-white/10 text-xs" data-testid="assign-view-btn">Load</button>
        </div>
        {existing.length > 0 && (
          <div className="max-h-64 overflow-y-auto space-y-1.5" data-testid="assign-existing-list">
            {existing.map((p) => (
              <div key={p.id} className="flex items-center gap-2 rounded bg-black/40 border border-white/10 p-1.5 text-xs">
                <img src={p.image_url} alt="" className="w-10 h-10 object-cover rounded" />
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-cyan-200 truncate">{p.name}</div>
                  <div className="text-slate-400 text-[10px] truncate">{p.category} · {(p.assigned_user_ids || []).length} user(s)</div>
                </div>
                <button onClick={() => deleteAssigned(p.id)} className="px-2 py-1 rounded bg-rose-500/20 text-rose-300 text-[10px]" data-testid={`assign-delete-${p.id.slice(0,8)}`}>Delete</button>
              </div>
            ))}
          </div>
        )}
        {viewUid && existing.length === 0 && (
          <div className="text-xs text-slate-500 italic">No private items for this user.</div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Admin — 📊 Batch Analytics for share-link engagement tracking
// ─────────────────────────────────────────────────────────────
function BatchAnalyticsPanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [nudgeModal, setNudgeModal] = useState(null); // { batch_id, non_clickers, item_count, link }
  const [nudgeLoading, setNudgeLoading] = useState(false);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/admin/trial-room/batch-analytics', { params: { limit: 20 } });
      setRows(r.data?.batches || []);
    } catch (_) { /* silent */ }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const openNudge = async (batchId) => {
    setNudgeLoading(true);
    try {
      const r = await api.get(`/admin/trial-room/batch-non-clickers/${batchId}`);
      const link = `${window.location.origin}/trial-room?assigned_batch=${batchId}`;
      setNudgeModal({ ...r.data, link });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load');
    }
    setNudgeLoading(false);
  };
  return (
    <div className="rounded-2xl border-2 border-amber-400/40 bg-gradient-to-br from-amber-950/30 to-slate-900 p-4 mt-4" data-testid="batch-analytics-panel">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-amber-300 font-black">📊 SHARE-LINK ENGAGEMENT</div>
          <h3 className="text-lg font-black">Batch Analytics</h3>
          <p className="text-xs text-slate-400">Har share-link ki performance — clicks · VTON tries · 👍/👎 per batch.</p>
        </div>
        <button onClick={load} disabled={loading} className="px-3 py-1.5 rounded-lg bg-white/10 text-xs flex items-center gap-1" data-testid="batch-analytics-refresh">
          <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>
      {rows.length === 0 ? (
        <div className="text-xs text-slate-400 italic">No batches yet. Assign items to users above → analytics automatic populate hongi.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="batch-analytics-table">
            <thead>
              <tr className="text-left text-slate-400 border-b border-white/10">
                <th className="py-1.5 pr-2">Batch (short)</th>
                <th className="py-1.5 pr-2">Created</th>
                <th className="py-1.5 pr-2 text-right">Items</th>
                <th className="py-1.5 pr-2 text-right">Users</th>
                <th className="py-1.5 pr-2 text-right">Clicks</th>
                <th className="py-1.5 pr-2 text-right">Click %</th>
                <th className="py-1.5 pr-2 text-right">VTON</th>
                <th className="py-1.5 pr-2 text-right">👍</th>
                <th className="py-1.5 pr-2 text-right">👎</th>
                <th className="py-1.5 pr-2 text-right">Nudge</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((b, i) => {
                const nonClickers = Math.max(0, (b.users_assigned || 0) - (b.unique_clicks || 0));
                return (
                <tr key={b.batch_id} className="border-b border-white/5" data-testid={`batch-analytics-row-${i}`}>
                  <td className="py-1.5 pr-2 font-mono text-amber-200">{b.batch_id.slice(0, 8)}…</td>
                  <td className="py-1.5 pr-2 text-slate-400">{(b.created_at || '').slice(0, 10)}</td>
                  <td className="py-1.5 pr-2 text-right">{b.item_count}</td>
                  <td className="py-1.5 pr-2 text-right">{b.users_assigned}</td>
                  <td className="py-1.5 pr-2 text-right font-bold text-cyan-300">{b.unique_clicks}</td>
                  <td className={`py-1.5 pr-2 text-right font-black ${b.click_rate_pct >= 50 ? 'text-emerald-300' : b.click_rate_pct >= 20 ? 'text-amber-300' : 'text-rose-300'}`}>{b.click_rate_pct}%</td>
                  <td className="py-1.5 pr-2 text-right text-fuchsia-300">{b.vton_tries}</td>
                  <td className="py-1.5 pr-2 text-right text-emerald-300 font-bold">{b.thumbs_up}</td>
                  <td className="py-1.5 pr-2 text-right text-rose-300 font-bold">{b.thumbs_down}</td>
                  <td className="py-1.5 pr-2 text-right">
                    <button
                      onClick={() => openNudge(b.batch_id)}
                      disabled={nonClickers === 0 || nudgeLoading}
                      title={nonClickers === 0 ? 'Sab clicked' : `Re-share to ${nonClickers} non-clickers`}
                      className="px-2 py-1 rounded bg-amber-500/20 border border-amber-500/40 text-amber-200 text-[10px] font-black disabled:opacity-30"
                      data-testid={`batch-nudge-btn-${i}`}
                    >🔄 {nonClickers}</button>
                  </td>
                </tr>
              );})}
            </tbody>
          </table>
        </div>
      )}
      <div className="mt-2 text-[10px] text-slate-500">
        💡 High click % + high VTON tries + 👍 &gt; 👎 = winning batch. Repeat that item mix for similar users.
      </div>

      {/* 🔄 Nudge Modal — WhatsApp re-share to non-clickers */}
      {nudgeModal && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => setNudgeModal(null)} data-testid="batch-nudge-modal">
          <div className="w-full max-w-lg rounded-2xl border border-amber-400/40 bg-slate-900 p-4 max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-2">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-amber-300 font-black">🔄 RE-SHARE NUDGE</div>
                <h4 className="text-base font-black">{nudgeModal.count} non-clickers</h4>
              </div>
              <button onClick={() => setNudgeModal(null)} className="text-slate-400 hover:text-white" data-testid="batch-nudge-close">✕</button>
            </div>
            {nudgeModal.count === 0 ? (
              <div className="text-xs text-emerald-300 py-4">🎉 Sab ne click kar diya! Koi nudge nahi chahiye.</div>
            ) : (
              <>
                <p className="text-xs text-slate-400 mb-2">Each user ka WhatsApp link pre-filled hai — click karo aur send karo:</p>
                <div className="space-y-1.5">
                  {nudgeModal.non_clickers.map((u) => {
                    const phone = (u.whatsapp_number || '').replace(/\D/g, '');
                    const msg = encodeURIComponent(`Hi ${u.name || ''}! 🎁 Aapke liye Anant Trial Room mein ${nudgeModal.item_count} items unlock hue hain — abhi tak try nahi kiya. Ek baar dekh lo: ${nudgeModal.link}`);
                    const url = phone ? `https://wa.me/${phone}?text=${msg}` : `https://wa.me/?text=${msg}`;
                    return (
                      <div key={u.id} className="flex items-center gap-2 rounded bg-black/40 border border-white/10 p-2 text-xs" data-testid={`batch-nudge-user-${u.id.slice(0,8)}`}>
                        <div className="flex-1 min-w-0">
                          <div className="font-bold text-cyan-200 truncate">{u.name || u.username}</div>
                          <div className="text-slate-400 text-[10px] truncate">@{u.username} · {phone || 'no whatsapp'}</div>
                        </div>
                        <a
                          href={url} target="_blank" rel="noopener noreferrer"
                          className="px-2 py-1 rounded bg-green-600 hover:bg-green-500 text-white text-[11px] font-black whitespace-nowrap"
                          data-testid={`batch-nudge-send-${u.id.slice(0,8)}`}
                        >📱 Send</a>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const KPI = ({ icon: Icon, label, val, color }) => (
  <div className={`rounded-xl border bg-gradient-to-br ${color} p-3`}>
    <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-slate-300"><Icon className="w-3 h-3" /> {label}</div>
    <div className="text-2xl font-black text-white mt-1">{val}</div>
  </div>
);

const MiniStat = ({ label, val, accent }) => (
  <div className="rounded-lg border border-white/10 bg-black/30 p-2">
    <div className="text-[9px] uppercase tracking-widest text-slate-400">{label}</div>
    <div className={`text-xl font-black ${accent || 'text-white'} font-mono`}>{val}</div>
  </div>
);
